/* eslint-disable prettier/prettier */
import React, { useEffect, useMemo, useState } from 'react'
import { RouteProp } from '@react-navigation/native'
import { observer } from 'mobx-react-lite'
import {
  APPROVED_STATUSES,
  LoanApplicationStatuses,
  PROCESSING_STATUSES,
} from '@linqpal/models/src/dictionaries'
import { PAGE_MODE } from '../InvoiceDetails/utils/pageMode'
import RootStore, { useStore } from '../../../store'
import { useGetInvoiceIdle } from './hooks/useGetInvoice'
import Loading from '../../Loading'
import {
  GoToAppButton,
  LoginButton,
} from '../InvoiceDetails/components/TopBarButtons'
import { SignInFlow } from '../SignIn/SignInFlow'
import { InvoiceDetails_v2 } from './flows/AuthFlow/InvoiceDetails_v2'
import { InvoiceDetailsIdle_v2 } from './flows/IdleFlow/InvoiceDetailsIdle_v2'
import { ScreenWrapper } from '../InvoiceDetails/components/ScreenWrapper'
import InvoicePayAsGuestFlow from './flows/PayAsGuestFlow/InvoicePayAsGuestFlow'

import { SignUpFlow } from '../SignUp/SignUpFlow'
import { SIGN_IN_STEPS, State } from '../SignIn/types'
import SignupStore from '../../Auth/stores/SignupStore'
import { STEPS } from './integrationSteps'
import { useIntegrationActions } from './hooks/useIntegrationActions'

interface IntegrationPaymentProps {
  route: RouteProp<
    {
      params: {
        invoiceId?: string
        mode?: PAGE_MODE
        redirectUrl?: string
        isGuest?: boolean
      }
    },
    'params'
  >
  navigation: any
}

export const IntegrationPayment: React.FC<IntegrationPaymentProps> = observer(
  ({ route, navigation }) => {
    const { userStore } = RootStore
    const { setValue, setLogin } = SignupStore
    const { remoteConfig } = useStore()
    const { isAuthenticated, setIsAuthenticated, setAuthState, user } =
      userStore

    const { invoiceId = '', mode, redirectUrl, isGuest } = route?.params ?? {}
    const [step, setStep] = useState(STEPS.IDLE)
    const [signInInitialStep, setSignInInitialStep] = useState(
      SIGN_IN_STEPS.SIGN_IN_LOGIN,
    )
    const [signInInitialState, setSignInInitialState] = useState<State>({})
    const [width, setWidth] = useState<number>(window.innerWidth)

    const isWeb = width > 575
    const minBnplAmount =
      (remoteConfig.get('de_min_approval_amount') as number) || 5

    useEffect(() => {
      const handleResize = () => setWidth(window.innerWidth)
      window.addEventListener('resize', handleResize)
      return () => window.removeEventListener('resize', handleResize)
    }, [])

    useEffect(() => {
      if (invoiceId) {
        if (isAuthenticated) {
          setStep(STEPS.INVOICE_DETAILS)
        } else if (isGuest) {
          setStep(STEPS.PAY_AS_GUEST)
        }
      }
    }, [invoiceId, isAuthenticated, isGuest])

    const {
      invoice,
      customer,
      invoiceCreditStatus,
      invoicePaymentStatus,
      loading,
      isInvoicePaid,
    } = useGetInvoiceIdle(invoiceId)

    const {
      handleNext,
      handlePayAsGuest,
      handleSignUpUserExists,
      handlePermissionDenied,
      handleLogin,
      handleLogOut,
      handleSignUp,
      handleAuthChange,
    } = useIntegrationActions({
      step,
      isAuthenticated,
      navigation,
      setStep,
      setAuthState,
      setIsAuthenticated,
      setSignInInitialState,
      setSignInInitialStep,
      customer,
      setValue,
      setLogin,
    })

    const isInvoiceCreditExist = [
      ...PROCESSING_STATUSES,
      ...APPROVED_STATUSES,
    ].includes(invoiceCreditStatus as LoanApplicationStatuses)

    const topBarButton = useMemo(() => {
      if (isAuthenticated) {
        return <GoToAppButton isWeb={isWeb} mode={mode} />
      }
      if (isInvoicePaid || isInvoiceCreditExist) {
        return <LoginButton isWeb={isWeb} onPress={handleLogin} />
      }
      return null
    }, [
      isAuthenticated,
      isInvoicePaid,
      isInvoiceCreditExist,
      isWeb,
      mode,
      handleLogin,
    ])

    const invoiceDetailsIdle = useMemo(
      () => (
        <InvoiceDetailsIdle_v2
          invoice={invoice}
          invoicePaymentStatus={invoicePaymentStatus}
          invoiceCreditExist={isInvoiceCreditExist}
          isInvoicePaid={isInvoicePaid}
          invoiceCreditStatus={invoiceCreditStatus}
          onPressLogin={handleLogin}
          onPressSignUp={handleSignUp}
          onPressPayAsGuest={handlePayAsGuest}
          isWeb={isWeb}
          customer={customer}
        />
      ),
      [
        invoice,
        invoicePaymentStatus,
        isInvoiceCreditExist,
        isInvoicePaid,
        invoiceCreditStatus,
        handleLogin,
        handleSignUp,
        handlePayAsGuest,
        isWeb,
        customer,
      ],
    )

    if (loading) {
      return <Loading />
    }

    if (!invoice || !invoiceId) {
      return null
    }

    return (
      <>
        {step === STEPS.SIGN_IN && (
          <SignInFlow
            onSuccess={handleNext}
            onSignUp={handleAuthChange}
            initialStep={signInInitialStep}
            initialState={signInInitialState}
            mode={mode}
          />
        )}
        {step === STEPS.SIGN_UP && (
          <SignUpFlow
            onSuccess={handleNext}
            onLogin={handleAuthChange}
            onUserExists={handleSignUpUserExists}
          />
        )}
        {step === STEPS.INVOICE_DETAILS && (
          <InvoiceDetails_v2
            invoiceId={invoiceId}
            minBnplAmount={minBnplAmount}
            onLogOut={handleLogOut}
            onPermissionDenied={handlePermissionDenied}
            mode={mode}
            redirectUrl={redirectUrl}
            isWeb={isWeb}
          />
        )}
        {step === STEPS.PAY_AS_GUEST && (
          <InvoicePayAsGuestFlow
            invoice={invoice}
            isInvoicePaidOff={isInvoicePaid || isInvoiceCreditExist}
            isWeb={isWeb}
            customer={customer}
            onSignUp={handleSignUp}
            mode={mode}
            redirectUrl={redirectUrl}
            onUserExists={handleSignUpUserExists}
          />
        )}
        {step === STEPS.IDLE &&
          (isWeb ? (
            <ScreenWrapper
              email={user?.email ? user.email : user?.login ? user.login : ''}
              isWeb={isWeb}
              button={topBarButton}
            >
              {invoiceDetailsIdle}
            </ScreenWrapper>
          ) : (
            invoiceDetailsIdle
          ))}
      </>
    )
  },
)
